const HEADER_SURFACE_STATE_EVENT = 'fit:header-surface-state';
const DESKTOP_MEDIA_QUERY = '(min-width: 750px)';
const DEFAULT_SCROLL_DISMISS_THRESHOLD = 50;
const DEFAULT_AUTO_HIDE_DELAY_SECONDS = 6;
const MIN_AUTO_HIDE_DELAY_SECONDS = 2;
const MAX_AUTO_HIDE_DELAY_SECONDS = 20;
const AUTO_HIDE_DISMISS_KEY_SUFFIX = ':auto-hide';

class IslandAnnouncementElement extends HTMLElement {
  #header = null;
  #headerObserver = null;
  #headerGroupObserver = null;
  #listenerController = null;
  #mediaQuery = null;
  #autoHideTimer = null;
  #autoHideDue = false;
  #isDesignMode = false;
  #stickyMode = 'follow_header';
  #dismissKey = '';
  #autoHideDismissKey = '';
  #isDismissed = false;
  #isDismissible = false;
  #usedLegacyMediaQueryListener = false;

  connectedCallback() {
    this.#teardown();

    this.#listenerController = new AbortController();
    this.#mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    this.#isDesignMode = this.hasAttribute('data-design-mode');
    this.#stickyMode = this.dataset.sticky || 'follow_header';
    this.#dismissKey = this.dataset.dismissKey?.trim() || '';
    this.#autoHideDismissKey = this.#autoHideSessionKey();
    const dismissButton = this.querySelector('[data-dismiss]');
    this.#isDismissible = this.dataset.dismissible === 'true' && dismissButton instanceof HTMLElement;
    this.#isDismissed = this.#readDismissedState();

    const { signal } = this.#listenerController;

    dismissButton?.addEventListener('click', this.#handleDismissClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    document.addEventListener(HEADER_SURFACE_STATE_EVENT, this.#handleHeaderSurfaceState, { signal });
    window.addEventListener('scroll', this.#handleWindowScroll, { passive: true, signal });

    if (this.#isDesignMode) {
      document.addEventListener('shopify:section:select', this.#handleEditorRestore, { signal });
      document.addEventListener('shopify:block:select', this.#handleEditorRestore, { signal });
      document.addEventListener('shopify:section:load', this.#handleEditorRestore, { signal });
    }

    if (typeof this.#mediaQuery.addEventListener === 'function') {
      this.#mediaQuery.addEventListener('change', this.#handleViewportChange, { signal });
    } else if (typeof this.#mediaQuery.addListener === 'function') {
      this.#usedLegacyMediaQueryListener = true;
      this.#mediaQuery.addListener(this.#handleViewportChange);
    }

    this.#bindHeader();
    this.#bindHeaderGroupObserver();
    this.#startAutoHideTimer();
    this.#syncVisibility();
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    this.#listenerController?.abort();
    this.#listenerController = null;
    this.#clearAutoHideTimer();
    this.#autoHideDue = false;

    if (this.#usedLegacyMediaQueryListener && this.#mediaQuery && typeof this.#mediaQuery.removeListener === 'function') {
      this.#mediaQuery.removeListener(this.#handleViewportChange);
    }

    this.#usedLegacyMediaQueryListener = false;
    this.#mediaQuery = null;

    this.#headerObserver?.disconnect();
    this.#headerObserver = null;

    this.#headerGroupObserver?.disconnect();
    this.#headerGroupObserver = null;

    this.#header = null;
    this.#autoHideDismissKey = '';
    this.#isDismissible = false;
  }

  #readDismissedState() {
    if (this.#isDesignMode) return false;

    if (this.#isDismissible && this.#readSessionFlag(this.#dismissKey)) return true;
    if (this.#stickyMode === 'auto_hide_after_delay') return this.#readSessionFlag(this.#autoHideDismissKey);

    return false;
  }

  #autoHideSessionKey() {
    const explicitKey = this.dataset.autoHideDismissKey?.trim();
    if (explicitKey) return explicitKey;

    if (!this.#dismissKey) return '';
    return `${this.#dismissKey}${AUTO_HIDE_DISMISS_KEY_SUFFIX}`;
  }

  #readSessionFlag(key) {
    if (!key) return false;

    try {
      return sessionStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }

  #writeSessionFlag(key) {
    if (!key) return;

    try {
      sessionStorage.setItem(key, '1');
    } catch {
      // Ignore storage failures in privacy-restricted browsing modes.
    }
  }

  #dismiss({ persist = false, persistAutoHide = false } = {}) {
    if (this.#isDismissed) return;

    this.#isDismissed = true;
    this.#autoHideDue = false;
    this.#clearAutoHideTimer();

    if (!this.#isDesignMode) {
      if (persist) this.#writeSessionFlag(this.#dismissKey);
      if (persistAutoHide) this.#writeSessionFlag(this.#autoHideDismissKey);
    }

    this.#syncVisibility({ moveFocus: true });
  }

  #canManuallyDismiss() {
    return this.#isDismissible;
  }

  #handleDismissClick = (event) => {
    event?.preventDefault();
    if (!this.#canManuallyDismiss()) return;

    this.#dismiss({ persist: true });
  };

  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#canManuallyDismiss()) return;
    event.preventDefault();
    this.#dismiss({ persist: true });
  };

  #handleEditorRestore = (event) => {
    if (!this.#isDesignMode) return;

    const eventSectionId = event?.detail?.sectionId;
    if (eventSectionId && eventSectionId !== this.dataset.sectionId) return;

    this.#isDismissed = false;
    this.#autoHideDue = false;
    this.#startAutoHideTimer();
    this.#syncVisibility();
  };

  #handleHeaderSurfaceState = (event) => {
    this.#bindHeader();

    if (
      !this.#isDesignMode &&
      this.#stickyMode === 'disappears_on_scroll' &&
      event?.detail?.phase === 'activate' &&
      event?.detail?.surfaceId
    ) {
      this.#dismiss();
      return;
    }

    this.#syncVisibility();
  };

  #handleWindowScroll = () => {
    if (this.#stickyMode !== 'disappears_on_scroll') {
      if (this.#stickyMode === 'follow_header' || this.#stickyMode === 'auto_hide_after_delay') {
        this.#syncVisibility();
      }
      return;
    }

    if (!this.#isDesignMode && window.scrollY > this.#scrollDismissThreshold()) {
      this.#dismiss();
      return;
    }

    this.#syncVisibility();
  };

  #handleViewportChange = () => {
    this.#syncVisibility();
  };

  #handleFocusOut = () => {
    if (!this.#shouldCompleteAutoHideAfterFocusLeaves()) return;

    window.setTimeout(this.#completeAutoHideAfterFocusLeaves, 0);
  };

  #handleAutoHideTimer = () => {
    this.#autoHideTimer = null;

    if (this.#stickyMode !== 'auto_hide_after_delay' || this.#isDismissed) return;

    if (this.#hasFocusWithin()) {
      this.#autoHideDue = true;
      return;
    }

    this.#dismissForAutoHide();
  };

  #startAutoHideTimer() {
    this.#clearAutoHideTimer();
    this.#autoHideDue = false;

    if (this.#stickyMode !== 'auto_hide_after_delay' || this.#isDismissed) return;

    this.#autoHideTimer = window.setTimeout(this.#handleAutoHideTimer, this.#autoHideDelayMilliseconds());
  }

  #clearAutoHideTimer() {
    if (this.#autoHideTimer === null) return;

    window.clearTimeout(this.#autoHideTimer);
    this.#autoHideTimer = null;
  }

  #autoHideDelayMilliseconds() {
    const explicitDelay = Number(this.dataset.autoHideDelaySeconds);
    const seconds = Number.isFinite(explicitDelay) ? explicitDelay : DEFAULT_AUTO_HIDE_DELAY_SECONDS;
    const boundedSeconds = Math.min(
      MAX_AUTO_HIDE_DELAY_SECONDS,
      Math.max(MIN_AUTO_HIDE_DELAY_SECONDS, seconds),
    );

    return boundedSeconds * 1000;
  }

  #hasFocusWithin() {
    const activeElement = document.activeElement;
    return activeElement instanceof Node && this.contains(activeElement);
  }

  #shouldCompleteAutoHideAfterFocusLeaves() {
    return (
      this.#stickyMode === 'auto_hide_after_delay' &&
      this.#autoHideDue &&
      !this.#isDismissed &&
      !this.#hasFocusWithin()
    );
  }

  #completeAutoHideAfterFocusLeaves = () => {
    if (!this.isConnected || !this.#shouldCompleteAutoHideAfterFocusLeaves()) return;

    this.#dismissForAutoHide();
  };

  #dismissForAutoHide() {
    this.#dismiss({ persistAutoHide: true });
  }

  #scrollDismissThreshold() {
    const explicitThreshold = Number(this.dataset.scrollDismissThreshold);
    if (Number.isFinite(explicitThreshold) && explicitThreshold >= 0) return explicitThreshold;
    return DEFAULT_SCROLL_DISMISS_THRESHOLD;
  }

  #isDesktopViewport() {
    return this.#mediaQuery?.matches ?? window.innerWidth >= 750;
  }

  #bindHeader() {
    const nextHeader = document.getElementById('fit-site-header');
    if (!(nextHeader instanceof HTMLElement)) {
      this.#headerObserver?.disconnect();
      this.#headerObserver = null;
      this.#header = null;
      return;
    }

    if (this.#header === nextHeader && this.#headerObserver) return;

    this.#headerObserver?.disconnect();
    this.#header = nextHeader;
    this.#headerObserver = new MutationObserver(() => {
      this.#bindHeader();
      this.#syncVisibility();
    });

    this.#headerObserver.observe(nextHeader, {
      attributes: true,
      attributeFilter: ['sticky', 'data-active-surface', 'data-sticky-state', 'data-animating'],
    });
  }

  #bindHeaderGroupObserver() {
    const headerGroup = document.getElementById('fit-header-group');
    if (!(headerGroup instanceof HTMLElement)) return;

    this.#headerGroupObserver?.disconnect();
    this.#headerGroupObserver = new MutationObserver(() => {
      const currentHeader = document.getElementById('fit-site-header');
      if (currentHeader !== this.#header) {
        this.#bindHeader();
      }
      this.#syncVisibility();
    });

    this.#headerGroupObserver.observe(headerGroup, { childList: true, subtree: true });
  }

  #shouldHideForFollowHeader() {
    const header = this.#header;
    if (!(header instanceof HTMLElement)) return false;

    if (header.dataset.activeSurface) return true;

    const shouldFollowStickyState = this.#isDesktopViewport() || header.hasAttribute('transparent-mobile');
    if (!shouldFollowStickyState) return false;

    if (header.getAttribute('sticky') !== 'scroll-up') return false;

    const stickyState = header.dataset.stickyState || '';
    const isAnimatingOut = stickyState === 'active' && header.hasAttribute('data-animating');
    return stickyState === 'idle' || isAnimatingOut;
  }

  #shouldPreviewDisappearInDesignMode() {
    if (this.#stickyMode !== 'disappears_on_scroll' || !this.#isDesignMode) return false;

    const pastThreshold = window.scrollY > this.#scrollDismissThreshold();
    return pastThreshold;
  }

  #shouldHideTemporarily() {
    if (this.#stickyMode === 'follow_header') {
      return this.#shouldHideForFollowHeader();
    }

    if (this.#stickyMode === 'auto_hide_after_delay') {
      return this.#shouldHideForFollowHeader();
    }

    return this.#shouldPreviewDisappearInDesignMode();
  }

  #restoreFocusAway() {
    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLElement) || !this.contains(activeElement)) return;

    const headerFocusable = document.querySelector(
      '#fit-site-header button, #fit-site-header [href], #fit-site-header summary, #fit-site-header input, #fit-site-header [tabindex]:not([tabindex="-1"])',
    );
    if (headerFocusable instanceof HTMLElement) {
      headerFocusable.focus({ preventScroll: true });
      return;
    }

    const mainContent = document.querySelector('main') || document.getElementById('MainContent');
    if (mainContent instanceof HTMLElement) {
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus({ preventScroll: true });
    }
  }

  #syncVisibility({ moveFocus = false } = {}) {
    this.#bindHeader();

    if (!this.#isDismissed && !this.#isDesignMode && this.#stickyMode === 'disappears_on_scroll') {
      if (window.scrollY > this.#scrollDismissThreshold()) {
        this.#dismiss();
        return;
      }

      if (this.#header?.dataset.activeSurface) {
        this.#dismiss();
        return;
      }
    }

    if (this.#shouldCompleteAutoHideAfterFocusLeaves()) {
      this.#dismissForAutoHide();
      return;
    }

    const shouldHide = this.#isDismissed || this.#shouldHideTemporarily();
    const wasHidden = this.hidden;
    if (shouldHide && (!wasHidden || moveFocus)) this.#restoreFocusAway();

    this.hidden = shouldHide;
    if (shouldHide) {
      this.setAttribute('aria-hidden', 'true');
    } else {
      this.removeAttribute('aria-hidden');
    }
    this.toggleAttribute('inert', shouldHide);
  }
}

if (!customElements.get('island-announcement')) {
  customElements.define('island-announcement', IslandAnnouncementElement);
}
